// src/pages/finance-portal/inventory/POForm.js
// Create/edit a purchase order with line items.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert, Autocomplete, Box, Button, CircularProgress, Divider,
    FormControl, Grid, IconButton, InputAdornment, InputLabel,
    MenuItem, Paper, Select, Stack, Table, TableBody, TableCell,
    TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import axiosInstance from '../../../utils/axiosInstance';
import {
    FONT_FAMILY, INV_ACCENT, INV_ACCENT_DARK, formatCurrency,
} from './components/inventoryFormatters';

const PO_TYPES = ['STANDARD', 'DROPSHIP', 'BLANKET', 'SERVICE', 'SUBCONTRACT'];

const EMPTY_LINE = {
    product_id: null, product: null, description: '', sku: '',
    ordered_qty: 1, unit_price: 0, discount_amount: 0, tax_rate: 0, uom: 'EA',
};

const EMPTY_PO = {
    po_type: 'STANDARD', vendor_id: null, vendor: null,
    vendor_quote_number: '', ship_to_location_id: null,
    shipping_method: '', order_date: new Date().toISOString().slice(0, 10),
    expected_delivery_date: '', required_by_date: '',
    currency: 'USD', exchange_rate: 1,
    shipping_amount: 0, discount_amount: 0, landed_cost_estimate: 0,
    payment_terms: 'NET_30', fob_terms: '', incoterms: '',
    notes: '', internal_notes: '',
    lines: [{ ...EMPTY_LINE }],
};

export default function POForm() {
    const { poId } = useParams();
    const navigate = useNavigate();
    const editing = Boolean(poId);

    const [form, setForm] = useState(EMPTY_PO);
    const [vendors, setVendors] = useState([]);
    const [locations, setLocations] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(editing);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [errors, setErrors] = useState({});

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const updateLine = (i, k, v) => {
        const next = form.lines.slice();
        next[i] = { ...next[i], [k]: v };
        setForm((f) => ({ ...f, lines: next }));
    };

    const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { ...EMPTY_LINE }] }));
    const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

    const load = useCallback(async () => {
        if (!editing) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/finance/inventory/purchase-orders/${poId}`);
            if (res.data?.success) {
                const d = res.data.data;
                setForm({
                    ...EMPTY_PO,
                    ...d,
                    order_date: d.order_date || '',
                    expected_delivery_date: d.expected_delivery_date || '',
                    required_by_date: d.required_by_date || '',
                    vendor: d.vendor_name ? { id: d.vendor_id, display_name: d.vendor_name } : null,
                    lines: (d.lines || []).map((l) => ({
                        ...EMPTY_LINE,
                        ...l,
                        product: l.product_name ? { id: l.product_id, name: l.product_name, sku: l.sku } : null,
                    })),
                });
            }
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load PO');
        } finally {
            setLoading(false);
        }
    }, [editing, poId]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        axiosInstance.get('/api/finance/vendors', { params: { per_page: 200 } })
            .then((r) => setVendors(r.data?.data || [])).catch(() => {});
        axiosInstance.get('/api/finance/inventory/locations')
            .then((r) => setLocations(r.data?.data || [])).catch(() => {});
        axiosInstance.get('/api/finance/inventory/products', { params: { per_page: 300 } })
            .then((r) => setProducts(r.data?.data || [])).catch(() => {});
    }, []);

    const subtotal = form.lines.reduce((sum, l) => {
        const qty = Number(l.ordered_qty) || 0;
        const up = Number(l.unit_price) || 0;
        const disc = Number(l.discount_amount) || 0;
        return sum + qty * up - disc;
    }, 0);
    const taxTotal = form.lines.reduce((sum, l) => {
        const qty = Number(l.ordered_qty) || 0;
        const up = Number(l.unit_price) || 0;
        const disc = Number(l.discount_amount) || 0;
        const rate = Number(l.tax_rate) || 0;
        return sum + ((qty * up) - disc) * (rate / 100);
    }, 0);
    const grandTotal = subtotal + taxTotal + Number(form.shipping_amount || 0) - Number(form.discount_amount || 0);

    const handleSave = async () => {
        if (!form.vendor_id && !form.vendor?.id) {
            setError('Vendor required');
            return;
        }
        if (!form.lines.length) {
            setError('At least one line is required');
            return;
        }
        setSaving(true); setError(null);

        const payload = {
            ...form,
            vendor_id: form.vendor?.id || form.vendor_id,
            lines: form.lines.map((l, idx) => ({
                line_number: idx + 1,
                product_id: l.product?.id || l.product_id || null,
                description: l.description,
                sku: l.sku || (l.product?.sku),
                ordered_qty: Number(l.ordered_qty) || 0,
                unit_price: Number(l.unit_price) || 0,
                discount_amount: Number(l.discount_amount) || 0,
                tax_rate: Number(l.tax_rate) || 0,
                uom: l.uom || 'EA',
                expected_delivery_date: l.expected_delivery_date || null,
                notes: l.notes,
            })),
        };

        try {
            if (editing) {
                await axiosInstance.put(`/api/finance/inventory/purchase-orders/${poId}`, payload);
                navigate(`/finance-portal/inventory/purchase-orders/${poId}`);
            } else {
                const res = await axiosInstance.post('/api/finance/inventory/purchase-orders', payload);
                if (res.data?.success) navigate(`/finance-portal/inventory/purchase-orders/${res.data.data.id}`);
            }
        } catch (e) {
            const serverErrors = e.response?.data?.errors;
            const msg = e.response?.data?.message || 'Save failed';
            if (serverErrors && typeof serverErrors === 'object') {
                setErrors(serverErrors);
            }
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, fontFamily: FONT_FAMILY, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <IconButton onClick={() => navigate(-1)}><ArrowBackIcon /></IconButton>
                    <Box>
                        <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.55rem', fontWeight: 700, color: '#0f172a' }}>
                            {editing ? `Edit ${form.po_number}` : 'New Purchase Order'}
                        </Typography>
                    </Box>
                </Stack>
                <Button variant="contained" startIcon={<SaveIcon />} disabled={saving} onClick={handleSave}
                    sx={{ fontFamily: FONT_FAMILY, textTransform: 'none',
                          bgcolor: INV_ACCENT, '&:hover': { bgcolor: INV_ACCENT_DARK } }}>
                    {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save'}
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <Paper sx={{ p: 3, mb: 3, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '1.05rem', mb: 2 }}>
                    PO Details
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <Autocomplete size="small" options={vendors}
                            getOptionLabel={(o) => o.display_name || o.company_name || o.vendor_number || ''}
                            value={form.vendor}
                            onChange={(e, v) => set('vendor', v)}
                            renderInput={(params) => <TextField {...params} label="Vendor" required />}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>PO Type</InputLabel>
                            <Select value={form.po_type} label="PO Type"
                                onChange={(e) => set('po_type', e.target.value)}>
                                {PO_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Ship To Location</InputLabel>
                            <Select value={form.ship_to_location_id || ''} label="Ship To Location"
                                onChange={(e) => set('ship_to_location_id', e.target.value)}>
                                <MenuItem value="">(none)</MenuItem>
                                {locations.map((l) => (
                                    <MenuItem key={l.id} value={l.id}>{l.code} — {l.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField size="small" fullWidth label="Order Date" type="date"
                            value={form.order_date || ''} onChange={(e) => set('order_date', e.target.value)}
                            InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField size="small" fullWidth label="Expected Delivery" type="date"
                            value={form.expected_delivery_date || ''}
                            onChange={(e) => set('expected_delivery_date', e.target.value)}
                            InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField size="small" fullWidth label="Required By" type="date"
                            value={form.required_by_date || ''}
                            onChange={(e) => set('required_by_date', e.target.value)}
                            InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField size="small" fullWidth label="Vendor Quote #"
                            value={form.vendor_quote_number || ''}
                            onChange={(e) => set('vendor_quote_number', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField size="small" fullWidth label="Payment Terms"
                            value={form.payment_terms} onChange={(e) => set('payment_terms', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField size="small" fullWidth label="Currency"
                            value={form.currency} onChange={(e) => set('currency', e.target.value.toUpperCase())} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField size="small" fullWidth label="Incoterms"
                            value={form.incoterms || ''} onChange={(e) => set('incoterms', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField size="small" fullWidth label="Shipping Method"
                            value={form.shipping_method || ''}
                            onChange={(e) => set('shipping_method', e.target.value)} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField size="small" fullWidth multiline rows={2} label="Notes (visible to vendor)"
                            value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField size="small" fullWidth multiline rows={2} label="Internal Notes"
                            value={form.internal_notes || ''}
                            onChange={(e) => set('internal_notes', e.target.value)} />
                    </Grid>
                </Grid>
            </Paper>

            <Paper sx={{ p: 3, mb: 3, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '1.05rem' }}>
                        Line Items
                    </Typography>
                    <Button size="small" startIcon={<AddIcon />} onClick={addLine}
                        sx={{ fontFamily: FONT_FAMILY, textTransform: 'none', color: INV_ACCENT }}>
                        Add Line
                    </Button>
                </Stack>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, width: '25%' }}>Product</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Description</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Qty</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Unit Price</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Tax %</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Line Total</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {form.lines.map((ln, i) => {
                            const qty = Number(ln.ordered_qty) || 0;
                            const up = Number(ln.unit_price) || 0;
                            const disc = Number(ln.discount_amount) || 0;
                            const lineTotal = qty * up - disc;
                            return (
                                <TableRow key={i}>
                                    <TableCell>
                                        <Autocomplete size="small" options={products}
                                            getOptionLabel={(o) => `${o.sku} — ${o.name}`}
                                            value={ln.product}
                                            onChange={(e, v) => {
                                                updateLine(i, 'product', v);
                                                if (v) {
                                                    updateLine(i, 'sku', v.sku);
                                                    updateLine(i, 'description', v.name);
                                                    updateLine(i, 'unit_price', v.unit_cost || 0);
                                                    updateLine(i, 'uom', v.unit_of_measure || 'EA');
                                                }
                                            }}
                                            renderInput={(params) => <TextField {...params} placeholder="Select SKU" />}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField size="small" fullWidth
                                            value={ln.description || ''}
                                            onChange={(e) => updateLine(i, 'description', e.target.value)} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <TextField size="small" type="number" sx={{ width: 90 }}
                                            value={ln.ordered_qty}
                                            onChange={(e) => updateLine(i, 'ordered_qty', e.target.value)} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <TextField size="small" type="number" sx={{ width: 110 }}
                                            value={ln.unit_price}
                                            onChange={(e) => updateLine(i, 'unit_price', e.target.value)}
                                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <TextField size="small" type="number" sx={{ width: 80 }}
                                            value={ln.tax_rate}
                                            onChange={(e) => updateLine(i, 'tax_rate', e.target.value)} />
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>
                                        {formatCurrency(lineTotal, form.currency)}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton size="small" onClick={() => removeLine(i)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Paper>

            <Paper sx={{ p: 3, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField size="small" fullWidth label="Shipping Amount" type="number"
                            value={form.shipping_amount}
                            onChange={(e) => set('shipping_amount', e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField size="small" fullWidth label="PO Discount" type="number"
                            value={form.discount_amount}
                            onChange={(e) => set('discount_amount', e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField size="small" fullWidth label="Landed Cost Estimate" type="number"
                            value={form.landed_cost_estimate}
                            onChange={(e) => set('landed_cost_estimate', e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                    </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Stack direction="row" justifyContent="flex-end" spacing={3}>
                    <Box sx={{ minWidth: 280 }}>
                        <Stack direction="row" justifyContent="space-between">
                            <Typography sx={{ fontFamily: FONT_FAMILY }}>Subtotal</Typography>
                            <Typography sx={{ fontFamily: FONT_FAMILY }}>{formatCurrency(subtotal, form.currency)}</Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                            <Typography sx={{ fontFamily: FONT_FAMILY }}>Tax</Typography>
                            <Typography sx={{ fontFamily: FONT_FAMILY }}>{formatCurrency(taxTotal, form.currency)}</Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                            <Typography sx={{ fontFamily: FONT_FAMILY }}>Shipping</Typography>
                            <Typography sx={{ fontFamily: FONT_FAMILY }}>{formatCurrency(form.shipping_amount, form.currency)}</Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                            <Typography sx={{ fontFamily: FONT_FAMILY }}>PO Discount</Typography>
                            <Typography sx={{ fontFamily: FONT_FAMILY, color: '#dc2626' }}>−{formatCurrency(form.discount_amount, form.currency)}</Typography>
                        </Stack>
                        <Divider sx={{ my: 1 }} />
                        <Stack direction="row" justifyContent="space-between">
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, fontSize: '1.05rem' }}>Grand Total</Typography>
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, fontSize: '1.05rem', color: INV_ACCENT_DARK }}>
                                {formatCurrency(grandTotal, form.currency)}
                            </Typography>
                        </Stack>
                    </Box>
                </Stack>
            </Paper>
        </Box>
    );
}
